/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { favoriteService } from '../services/favoriteService'
import type { FavoriteIdentity } from '../services/favoriteService'
import { useUser } from './UserContext'
import React from 'react'

interface FavoritesContextValue {
  isFavorite: (identity: FavoriteIdentity) => boolean
  toggle: (identity: FavoriteIdentity) => Promise<void>
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null)

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser()
  const [favoriteUserIds, setFavoriteUserIds] = useState<Set<number>>(new Set())
  const [favoriteExternalIds, setFavoriteExternalIds] = useState<Set<string>>(new Set())

  const isPlayer = user?.roles?.includes('PLAYER')

  useEffect(() => {
    const reset = () => {
      setFavoriteUserIds(new Set())
      setFavoriteExternalIds(new Set())
    }

    if (!isPlayer) {
      reset()
      return
    }

    favoriteService.getFavorites()
      .then(({ userIds, externalPlayerIds }) => {
        setFavoriteUserIds(new Set(userIds))
        setFavoriteExternalIds(new Set(externalPlayerIds))
      })
      .catch(reset)
  }, [isPlayer])

  const isFavorite = useCallback((identity: FavoriteIdentity): boolean => {
    if (identity.userId != null && favoriteUserIds.has(identity.userId)) return true
    if (identity.externalPlayerId != null && favoriteExternalIds.has(identity.externalPlayerId)) return true
    return false
  }, [favoriteUserIds, favoriteExternalIds])

  const toggle = useCallback(async (identity: FavoriteIdentity): Promise<void> => {
    const wasFavoriteByUserId = identity.userId != null && favoriteUserIds.has(identity.userId)
    const wasFavoriteByExternal = identity.externalPlayerId != null && favoriteExternalIds.has(identity.externalPlayerId)
    const wasFavorite = wasFavoriteByUserId || wasFavoriteByExternal

    if (wasFavorite) {
      // Remove whichever identity actually matched — prefer userId if it matched
      const removeTarget: FavoriteIdentity = wasFavoriteByUserId
        ? { userId: identity.userId }
        : { externalPlayerId: identity.externalPlayerId }

      // Optimistic update
      if (wasFavoriteByUserId && identity.userId != null) {
        const uid = identity.userId
        setFavoriteUserIds(prev => {
          const next = new Set(prev)
          next.delete(uid)
          return next
        })
      } else if (identity.externalPlayerId != null) {
        const eid = identity.externalPlayerId
        setFavoriteExternalIds(prev => {
          const next = new Set(prev)
          next.delete(eid)
          return next
        })
      }

      try {
        await favoriteService.removeFavorite(removeTarget)
      } catch {
        // Revert on failure
        if (wasFavoriteByUserId && identity.userId != null) {
          const uid = identity.userId
          setFavoriteUserIds(prev => new Set(prev).add(uid))
        } else if (identity.externalPlayerId != null) {
          const eid = identity.externalPlayerId
          setFavoriteExternalIds(prev => new Set(prev).add(eid))
        }
      }
    } else {
      // Add by precedence: userId first, else externalPlayerId
      const addByUserId = identity.userId != null

      // Optimistic update
      if (addByUserId && identity.userId != null) {
        const uid = identity.userId
        setFavoriteUserIds(prev => new Set(prev).add(uid))
      } else if (identity.externalPlayerId != null) {
        const eid = identity.externalPlayerId
        setFavoriteExternalIds(prev => new Set(prev).add(eid))
      }

      const addTarget: FavoriteIdentity = addByUserId
        ? { userId: identity.userId }
        : { externalPlayerId: identity.externalPlayerId }

      try {
        await favoriteService.addFavorite(addTarget)
      } catch {
        // Revert on failure
        if (addByUserId && identity.userId != null) {
          const uid = identity.userId
          setFavoriteUserIds(prev => {
            const next = new Set(prev)
            next.delete(uid)
            return next
          })
        } else if (identity.externalPlayerId != null) {
          const eid = identity.externalPlayerId
          setFavoriteExternalIds(prev => {
            const next = new Set(prev)
            next.delete(eid)
            return next
          })
        }
      }
    }
  }, [favoriteUserIds, favoriteExternalIds])

  const value: FavoritesContextValue = useMemo(
    () => ({ isFavorite, toggle }),
    [isFavorite, toggle]
  )

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}

export const useFavorites = (): FavoritesContextValue => {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}
